import { useMemo, useState } from 'react'
import { round } from 'shared/util'

import { Loading } from 'components/controls/loading'

import { DialogBase } from 'components/dialog'
import { DialogCaptionCloseButton } from 'components/dialog/components/caption'

import {
  Typography,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Tabs,
  Tab,
  Chip,
  DialogTitle,
  Box
} from '@mui/material'

import { deepOrange, deepPurple } from '@mui/material/colors'

import { useGetTargetListReason } from 'interface/search/targetList'

type ColorDepses = '50' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900'

type TargetListReasonDialogProps = {
  searchId: number
  listId: number
  onClose: () => void
}
export const TargetListReasonDialog = ({
  searchId,
  listId,
  onClose,
}: TargetListReasonDialogProps) => {
  const [currentTab, setCurrentTab] = useState<'positive' | 'negative'>('positive')

  const reasonState = useGetTargetListReason(searchId, listId)
  const reasonList = useMemo(
    () => {
      const list = currentTab === 'positive' ? reasonState.data?.positive : reasonState.data?.negative
      if (!list || list.length === 0) return null

      const priorityMax = list.reduce((s, r) => Math.max(r.priority, s), 0)
      if (priorityMax === 0) return null

      return list.map((reason) => {
        const priorityRate = reason.priority / priorityMax
        const level: {
          colorDeps: ColorDepses
          avatarFontSize: string
          avatarSize: number
          textColor: string
          fontSize: string
        } = priorityRate < 0.1 ? {
          colorDeps: '50',
          avatarFontSize: '45%',
          avatarSize: 25,
          textColor: 'text.secondary',
          fontSize: '80%',
        } : priorityRate < 0.2 ? {
          colorDeps: '100',
          avatarFontSize: '45%',
          avatarSize: 25,
          textColor: 'text.secondary',
          fontSize: '80%',
        } : priorityRate < 0.3 ? {
          colorDeps: '200',
          avatarFontSize: '50%',
          avatarSize: 30,
          textColor: 'text.primary',
          fontSize: '100%',
        } : priorityRate < 0.4 ? {
          colorDeps: '300',
          avatarFontSize: '50%',
          avatarSize: 30,
          textColor: 'text.primary',
          fontSize: '100%',
        } : priorityRate < 0.5 ? {
          colorDeps: '400',
          avatarFontSize: '50%',
          avatarSize: 30,
          textColor: 'text.primary',
          fontSize: '100%',
        } : priorityRate < 0.6 ? {
          colorDeps: '500',
          avatarFontSize: '60%',
          avatarSize: 35,
          textColor: 'text.primary',
          fontSize: '120%',
        } : priorityRate < 0.7 ? {
          colorDeps: '600',
          avatarFontSize: '60%',
          avatarSize: 35,
          textColor: 'text.primary',
          fontSize: '120%',
        } : priorityRate < 0.8 ? {
          colorDeps: '700',
          avatarFontSize: '60%',
          avatarSize: 35,
          textColor: 'text.primary',
          fontSize: '120%',
        } : priorityRate < 0.9 ? {
          colorDeps: '800',
          avatarFontSize: '80%',
          avatarSize: 45,
          textColor: 'text.primary',
          fontSize: '150%',
        } : {
          colorDeps: '900',
          avatarFontSize: '80%',
          avatarSize: 45,
          textColor: 'text.primary',
          fontSize: '150%',
        }
        return {
          reason,
          level
        }
      })
    },
    [currentTab, reasonState]
  )

  if (reasonState.loading) return <Loading />
  return (
    <DialogBase
      open
      fullWidth
      maxWidth="md"
      titleElement={
        <DialogTitle sx={{ paddingBottom: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tabs
              value={currentTab}
              onChange={(ev, value) => setCurrentTab(value)}
              sx={{ flexShrink: 0 }}
              scrollButtons="auto"
              aria-label="ranking-preview-tabs"
            >
              <Tab
                label={
                  <Stack direction="row" spacing={1}>
                    <Typography variant="body1" color="text.primary">
                      組入条件
                    </Typography>
                    <Chip
                      label={reasonState.data?.positive.length}
                      color="primary"
                      sx={{ bgcolor: deepOrange[500] }}
                      size="small"
                    />
                  </Stack>
                }
                value="positive"
                id="reason-positive-tab"
              />
              <Tab
                label={
                  <Stack direction="row" spacing={1}>
                    <Typography variant="body1" color="text.primary">
                      排除条件
                    </Typography>
                    <Chip
                      label={reasonState.data?.negative.length}
                      color="primary"
                      sx={{ bgcolor: deepPurple[500] }}
                      size="small"
                    />
                  </Stack>
                }
                value="negative"
                id="reason-negative-tab"
              />
            </Tabs>

            <Box flexGrow={1}/>

            <DialogCaptionCloseButton onClose={onClose}/>
          </Stack>
        </DialogTitle>
      }
      error={reasonState.error}
      body={
        <List dense>
          {reasonList?.map(({ reason, level }, i) => (
            <ListItem key={`reason-${i}`}>
              <ListItemAvatar sx={{ marginRight: 1 }}>
                <Avatar
                  sx={{
                    bgcolor:
                      currentTab === 'positive'
                        ? deepOrange[level.colorDeps]
                        : deepPurple[level.colorDeps],
                    width: level.avatarSize,
                    height: level.avatarSize,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      display: 'block',
                      textAlign: 'center',
                      fontSize: level.avatarFontSize,
                    }}
                  >
                    {round(reason.priority, 2)}
                  </Typography>
                </Avatar>
              </ListItemAvatar>

              <ListItemText
                primary={reason.reason}
                primaryTypographyProps={{
                  variant: 'body2',
                  color: level.textColor,
                  fontSize: level.fontSize,
                }}
              />
            </ListItem>
          ))}
        </List>
      }
      onClose={() => {
        onClose()
      }}
      contentProps={{
        sx: {
          height: '60vh'
        }
      }}
    />
  )
}
